import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ServerStatusBadge } from "@/components/DocStatus";
import { ProviderRoster } from "@/components/admin/ProviderRoster";
import { UserCog, Phone, Check, X, LayoutGrid, List, ArrowUpDown, ChevronRight } from "lucide-react";
import { AccountArchiveControls } from "@/components/admin/AccountArchiveControls";

const FILTER_LABELS = {
  all: "All servers", account_complete: "Account complete", docs_in_progress: "Documents in progress",
  docs_approved: "Fully document-approved", pending_approval: "Pending account approval",
  active: "Approved / active", rejected: "Rejected", missing_docs: "Missing documents",
};

export default function AdminServers() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = params.get("filter") || "all";
  const [servers, setServers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [view, setView] = useState("card");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [tab, setTab] = useState("roster");

  const [showArchived, setShowArchived] = useState(false);
  const load = React.useCallback(() => {
    api.get(`/servers${showArchived ? "?archived=true" : ""}`).then(({ data }) => setServers(data)).catch(() => {});
  }, [showArchived]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action, e) => {
    e?.stopPropagation();
    setBusy(id + action);
    try {
      await api.post(`/servers/${id}/${action}`, { notes: "" });
      toast.success(action === "approve" ? "Server approved" : "Server rejected");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const sorted = useMemo(() => {
    const matches = (s) => {
      const total = s.docs_summary?.total || 6;
      const approved = s.docs_summary?.approved || 0;
      const pending = s.docs_summary?.pending || 0;
      switch (filter) {
        case "pending_approval": return s.server_status === "pending_approval";
        case "rejected": return s.server_status === "rejected";
        case "active": return (s.server_status || "active") === "active";
        case "account_complete": return !!s.account_complete;
        case "docs_approved": return approved >= total;
        case "missing_docs": return approved < total;
        case "docs_in_progress": return pending > 0;
        default: return true;
      }
    };
    const arr = servers.filter(matches);
    arr.sort((a, b) => {
      const av = (a[sort.key] ?? "").toString().toLowerCase();
      const bv = (b[sort.key] ?? "").toString().toLowerCase();
      if (typeof a[sort.key] === "number") return sort.dir === "asc" ? a[sort.key] - b[sort.key] : b[sort.key] - a[sort.key];
      return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [servers, sort, filter]);

  const columns = [
    { key: "name", label: "Name", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "server_status", label: "Status", sortable: true },
    { key: "eligibility", label: "Pools", sortable: false },
    { key: "agency_name", label: "Business", sortable: false },
    { key: "license_number", label: "License / Approval", sortable: true },
    { key: "active_jobs", label: "Active Jobs", sortable: true },
    { key: "completed_jobs", label: "Completed Jobs", sortable: true },
    { key: "workload", label: "Workload", sortable: false },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Providers</h2>
          <p className="text-slate-500">Review onboarding, approve or reject providers, and see workload.</p>
        </div>
        {tab === "directory" && (
          <div className="flex items-center gap-2">
            <button data-testid="servers-archived-toggle" onClick={() => setShowArchived((v) => !v)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${showArchived ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-600"}`}>
              {showArchived ? "Viewing archived" : "Archived"}
            </button>
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
              <button data-testid="view-toggle-card" onClick={() => setView("card")} className={`rounded-md p-1.5 ${view === "card" ? "bg-slate-900 text-white" : "text-slate-500"}`}><LayoutGrid className="h-4 w-4" /></button>
              <button data-testid="view-toggle-list" onClick={() => setView("list")} className={`rounded-md p-1.5 ${view === "list" ? "bg-slate-900 text-white" : "text-slate-500"}`}><List className="h-4 w-4" /></button>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2" data-testid="providers-view-toggle">
        <button data-testid="providers-view-roster" onClick={() => setTab("roster")}
          className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${tab === "roster" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          Provider roster
        </button>
        <button data-testid="providers-view-directory" onClick={() => setTab("directory")}
          className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${tab === "directory" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          Directory
        </button>
      </div>

      {tab === "roster" && <ProviderRoster />}

      {tab === "directory" && (<>
      {filter !== "all" && (
        <div data-testid="server-filter-chip" className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Filtered: <strong>{FILTER_LABELS[filter] || filter}</strong> ({sorted.length})
          <button data-testid="clear-server-filter" onClick={() => setParams({})} className="ml-1 font-semibold underline">Clear</button>
        </div>
      )}

      {view === "card" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sorted.map((s) => {
            const status = s.server_status || "active";
            return (
              <div key={s.id} data-testid={`server-card-${s.id}`} onClick={() => nav(`/admin/servers/${s.id}`)} title="Open server detail" className="group cursor-pointer rounded-xl border border-slate-200 bg-white p-5 transition hover:border-emerald-300 hover:shadow-md">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-indigo-100"><UserCog className="h-5 w-5 text-indigo-700" /></div>
                  <div className="min-w-0 flex-1">
                    <button data-testid={`server-name-link-${s.id}`} onClick={(e) => { e.stopPropagation(); nav(`/admin/servers/${s.id}`); }} className="truncate text-left font-display text-base font-bold text-slate-900 underline-offset-2 group-hover:text-emerald-600 group-hover:underline">{s.name}</button>
                    <p className="truncate text-xs text-slate-500">{s.email}</p>
                    {s.agency_name && <p data-testid={`server-business-${s.id}`} className="truncate text-[11px] font-semibold text-emerald-700">{s.agency_name}</p>}
                  </div>
                  <ServerStatusBadge status={status} testid={`server-status-${s.id}`} />
                </div>
                <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                  {s.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" /> {s.phone}</p>}
                </div>
                {s.docs_summary && (
                  <div className="mt-3" data-testid={`server-progress-${s.id}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-slate-500">Documents: {s.docs_summary.approved}/{s.docs_summary.total} approved</span>
                      <span className="text-slate-400">{s.docs_summary.pending} pending · {s.docs_summary.rejected} rejected</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.round((s.docs_summary.approved / s.docs_summary.total) * 100)}%` }} />
                    </div>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                  {s.process_server_eligible && <span data-testid={`badge-ps-${s.id}`} className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">PS ✓</span>}
                  {s.notary_eligible && <span data-testid={`badge-notary-${s.id}`} className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 font-semibold text-indigo-700">Notary ✓</span>}
                  <span className={`rounded-full border px-2 py-0.5 font-semibold ${s.account_complete ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>Account {s.account_complete ? "complete" : "incomplete"}</span>
                  <span className={`rounded-full border px-2 py-0.5 font-semibold ${s.docs_complete ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}>Docs {s.docs_complete ? "approved" : "pending"}</span>
                  <span className={`rounded-full border px-2 py-0.5 font-semibold ${(s.server_status || "active") === "active" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>Approval {(s.server_status || "active") === "active" ? "approved" : (s.server_status || "").replace("_", " ") || "pending"}</span>
                </div>
                {status === "pending_approval" ? (
                  <div className="mt-4 flex gap-2">
                    <Button data-testid={`approve-server-${s.id}`} size="sm" disabled={busy} onClick={(e) => decide(s.id, "approve", e)} className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                    <Button data-testid={`reject-server-${s.id}`} size="sm" variant="outline" disabled={busy} onClick={(e) => decide(s.id, "reject", e)} className="flex-1 border-red-300 text-red-600 hover:bg-red-50"><X className="mr-1 h-3.5 w-3.5" /> Reject</Button>
                  </div>
                ) : (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-lg bg-sky-50 px-3 py-2 text-center"><span className="font-display text-lg font-bold text-sky-700">{s.active_jobs}</span><p className="text-[11px] text-slate-500">Active</p></div>
                    <div className="rounded-lg bg-emerald-50 px-3 py-2 text-center"><span className="font-display text-lg font-bold text-emerald-700">{s.completed_jobs}</span><p className="text-[11px] text-slate-500">Completed</p></div>
                  </div>
                )}
                <Button data-testid={`open-server-detail-${s.id}`} size="sm" variant="outline" onClick={() => nav(`/admin/servers/${s.id}`)} className="mt-3 w-full">View detail <ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>
                <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                  <AccountArchiveControls id={s.id} name={s.name} role="server" archived={showArchived} onDone={load} />
                </div>
              </div>
            );
          })}
          {servers.length === 0 && <p className="col-span-full py-12 text-center text-slate-400">No providers registered yet.</p>}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="server-list-view">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="px-4 py-3">
                    {c.sortable ? (
                      <button data-testid={`sort-${c.key}`} onClick={() => toggleSort(c.key)} className="flex items-center gap-1 hover:text-slate-800">
                        {c.label} <ArrowUpDown className="h-3 w-3" />{sort.key === c.key && <span className="text-emerald-600">{sort.dir === "asc" ? "↑" : "↓"}</span>}
                      </button>
                    ) : c.label}
                  </th>
                ))}
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((s) => (
                <tr key={s.id} data-testid={`server-row-${s.id}`} className="border-t border-slate-50 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <button data-testid={`server-name-link-${s.id}`} onClick={() => nav(`/admin/servers/${s.id}`)} className="font-semibold text-slate-900 hover:text-emerald-600 hover:underline">{s.name}</button>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{s.email}</td>
                  <td className="px-4 py-3"><ServerStatusBadge status={s.server_status || "active"} /></td>
                  <td className="px-4 py-3">
                    <span className="flex flex-wrap gap-1 text-[11px]">
                      {s.process_server_eligible && <span data-testid={`badge-ps-row-${s.id}`} className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">PS ✓</span>}
                      {s.notary_eligible && <span data-testid={`badge-notary-row-${s.id}`} className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 font-semibold text-indigo-700">Notary ✓</span>}
                      {!s.process_server_eligible && !s.notary_eligible && <span className="text-slate-400">—</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{s.agency_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{s.license_number || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{s.active_jobs}</td>
                  <td className="px-4 py-3 text-slate-700">{s.completed_jobs}</td>
                  <td className="px-4 py-3 text-slate-600">{s.docs_summary ? `${s.docs_summary.approved}/${s.docs_summary.total} docs` : "—"}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <AccountArchiveControls id={s.id} name={s.name} role="server" archived={showArchived} onDone={load} />
                  </td>
                </tr>
              ))}
              {servers.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-400">No providers registered yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}
